/**
 * (c) 2023, Center for Computational Thinking and Design at Aarhus University and contributors
 * Modifications (c) 2024, Micro:bit Educational Foundation and contributors
 *
 * SPDX-License-Identifier: MIT
 */
import { ReactNode } from "react";
import type * as tf from "@tensorflow/tfjs";
import { SpotlightStyle } from "./pages/TourOverlay";
import { ModalSize } from "@microbit/ui";
import { MakeCodeIcon } from "./utils/icons";

/**
 * A trained model as plain, structured-cloneable data. The live
 * tf.LayersModel exists only in the ML worker; the main thread holds the
 * serialised artifacts (for persistence and rehydration in the worker) and
 * the ml4f machine code compiled from them (for MakeCode project
 * generation).
 */
export interface TrainedModel {
  artifacts: tf.io.ModelArtifacts;
  machineCode: Uint8Array;
}

export interface XYZData {
  x: number[];
  y: number[];
  z: number[];
}

export interface RecordingData {
  id: string;
  data: XYZData;
  createdAt: number;
}

export interface Action {
  name: string;
  id: string;
  icon: MakeCodeIcon;
  requiredConfidence?: number;
  createdAt: number;
}

export interface OldRecordingData {
  ID: number;
  data: XYZData;
}

export interface OldActionData
  extends Omit<ActionData, "id" | "recordings" | "createdAt"> {
  ID: number;
  recordings: OldRecordingData[];
}

export interface ActionData extends Action {
  recordings: RecordingData[];
}

export interface DatasetEditorJsonFormat {
  data: ActionData[];
}

export type DatasetUserFileFormat = ActionData[];

// Exported for testing
export const isDatasetUserFileFormat = (
  v: unknown
): v is DatasetUserFileFormat => {
  if (!Array.isArray(v)) {
    return false;
  }
  const array = v as unknown[];
  for (const item of array) {
    if (typeof item !== "object" || item === null) {
      return false;
    }
    if (
      !("name" in item) ||
      !("ID" in item || "id" in item) ||
      !("recordings" in item) ||
      !Array.isArray(item.recordings)
    ) {
      return false;
    }
    const recordings = item.recordings as unknown[];
    for (const rec of recordings) {
      if (typeof rec !== "object" || rec === null) {
        return false;
      }
      if (
        !("data" in rec) ||
        !("ID" in rec || "id" in rec) ||
        Array.isArray(rec.data)
      ) {
        return false;
      }
      const xyzData = rec.data as object;
      if (
        !("x" in xyzData) ||
        !("y" in xyzData) ||
        !("z" in xyzData) ||
        !Array.isArray(xyzData.x) ||
        !Array.isArray(xyzData.y) ||
        !Array.isArray(xyzData.z)
      ) {
        return false;
      }
    }
  }
  return true;
};

export interface HexData {
  /**
   * Hex data.
   */
  hex: string;
  /**
   * Filename without the .hex extension.
   */
  name: string;
}

export interface HexUrl {
  url: string;

  /**
   * Filename without the .hex extension.
   */
  name: string;
}

export const enum TrainModelDialogStage {
  Closed,
  InsufficientData,
  Help,
  TrainingError,
  TrainingInProgress,
}

export interface SaveState {
  step: SaveStep;
  hex?: HexData;
  type: SaveType;
}

export enum SaveType {
  Download = "download",
  Share = "share",
}

export enum SaveStep {
  None = "none",
  PreSaveHelp = "help",
  ProjectName = "project name",
  /**
   * We only show this state if we initiated the save and need to wait for the editor.
   * Otherwise we already have the project data in the state and save it directly.
   */
  SaveProgress = "progress",
  /**
   * Only used for the sharesheet on mobile devices
   */
  ChooseDestination = "choose destination",
}

export interface TourStep {
  selector?: string;
  title: ReactNode;
  content: ReactNode;
  spotlightStyle?: SpotlightStyle;
  /** Only used for steps without a selector (rendered as a centred modal). */
  modalSize?: ModalSize;
  /** Popover placement relative to the spotlighted element. */
  placement?: "top" | "bottom" | "left" | "right";
}

export interface TourState {
  steps: TourStep[];
  index: number;
  markCompleted: TourTriggerName[];
}

export const tourSequence: TourTriggerName[] = [
  "Connect",
  "DataSamplesRecorded",
  "TrainModel",
  "MakeCode",
];

export type TourTriggerName =
  | "Connect"
  | "DataSamplesRecorded"
  | "TrainModel"
  | "MakeCode";

export type TourTrigger =
  | { name: "Connect" }
  | { name: "MakeCode" }
  | { name: "TrainModel"; delayedUntilConnection: boolean }
  | { name: "DataSamplesRecorded"; recordingCount: number };

/**
 * Information passed omn the URL from microbit.org.
 * We call back into microbit.org to grab a JSON file with
 * full details.
 */
export type MicrobitOrgResource = {
  /**
   * ID that can be used when fetching the code from microbit.org.
   */
  id: string;

  /**
   * Name of the microbit.org project or lesson.
   *
   * We use this to load the target code.
   */
  project: string;

  /**
   * Name of the actual code snippet.
   * Due to a data issue this can often be the same as the project name.
   */
  name: string;
};

export enum DataSamplesView {
  Graph = "graph",
  DataFeatures = "data features",
  GraphAndDataFeatures = "graph and data features",
}

export type DataSamplesPageHint =
  | null
  | { type: "move-microbit" | "record-more-action" | "add-action" | "train" }
  | {
      type:
        | "name-action"
        | "record-action"
        | "name-action-with-samples"
        | "name-action-short";
      actionIdx: number;
    };

export enum PostImportDialogState {
  None = "none",
  Error = "error",
  NonCreateAiHex = "non create ai hex dialog",
}

export type EditorStartUp = "in-progress" | "timed out" | "done";
